import { useCallback, useMemo } from 'react';
import { AgentsService } from '@inference.engine/api-client/web';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@/entities/user';
import { inferenceHttp } from '@/lib/api/http-client.ts';
import { logger } from '@/lib/utils/logger';
import { i18n } from '@/shared/i18n';
import type { CategoryItem, ToolItem } from '@/shared/types/tool-categories.types';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { getSpringboardCategories } from '@/shared/api/springboard-categories.api';
import { SPRINGBOARD_CATEGORIES_QUERY_KEYS } from '@/shared/constants/springboard-categories-query-keys';
import { applyCategoryLabelOverrides } from '@/shared/utils/applyCategoryLabelOverrides';
import type { ManagedTool } from '@/shared/config/managedTools';
import { useVisibleManagedTools } from '../use-visible-managed-tools/useVisibleManagedTools';
import type { UseToolCategoriesOptions } from './useToolCategories.types';

// eslint-disable-next-line i18next/no-literal-string -- tool category ids; CATEGORY_LABELS carries the copy
const CATEGORY_TAGS = ['plan', 'create', 'scale', 'deliver', 'support'] as const;
type CategoryTag = (typeof CATEGORY_TAGS)[number];

// Thunks, not strings: a resolved label at module load would be read before
// i18n holds its resources.
const CATEGORY_LABELS: Record<CategoryTag, () => string> = {
  plan: () => i18n.t('tools:toolsHub.toolsHubConstants.plan'),
  create: () => i18n.t('tools:toolsHub.toolsHubConstants.create'),
  scale: () => i18n.t('tools:toolsHub.toolsHubConstants.scale'),
  deliver: () => i18n.t('tools:toolsHub.toolsHubConstants.deliver'),
  support: () => i18n.t('tools:toolsHub.toolsHubConstants.support'),
};

/**
 * Managed tools are synthetic (not returned by `getAllAgents`), so append the
 * currently-visible ones into their category by `tag`. Done here so every
 * consumer of this hook — notably the top nav dropdown — lists them alongside
 * real agents, matching the tools page. Deduped so a managed id never doubles
 * up with a real agent of the same id.
 */
const injectManagedTools = (
  categories: CategoryItem[],
  managedTools: ManagedTool[],
): CategoryItem[] => {
  if (managedTools.length === 0) {
    return categories;
  }
  return categories.map((category) => {
    const toolsForCategory = managedTools.filter((tool) => tool.tag === category.id);
    if (toolsForCategory.length === 0) {
      return category;
    }
    const existingIds = new Set(category.tools.map((tool) => tool.id));
    const managedItems: ToolItem[] = toolsForCategory
      .filter((tool) => !existingIds.has(tool.id))
      .map((tool) => ({ id: tool.id, label: tool.name }));
    return { ...category, tools: [...category.tools, ...managedItems] };
  });
};

export const useToolCategories = (options?: UseToolCategoriesOptions) => {
  const workspaceId = useCurrentWorkspaceId();
  const includeHidden = options?.includeHidden ?? false;
  const { canOpenTool } = usePermissions();
  const customizationQuery = useQuery({
    queryKey: SPRINGBOARD_CATEGORIES_QUERY_KEYS.categories(workspaceId ?? ''),
    queryFn: () => getSpringboardCategories(workspaceId ?? ''),
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000,
  });

  const filterByPermissions = useCallback(
    (categories: CategoryItem[]) =>
      categories.map((cat) => ({
        ...cat,
        tools: cat.tools.filter((tool) => canOpenTool(tool.id)),
      })),
    [canOpenTool],
  );

  const categoriesQuery = useQuery({
    queryKey: ['tool-categories', workspaceId, includeHidden],
    queryFn: async (): Promise<CategoryItem[]> => {
      try {
        const response = await AgentsService.getAllAgents({
          client: inferenceHttp,
        });
        const allAgents = response.data ?? [];

        return CATEGORY_TAGS.map((categoryTag: CategoryTag) => {
          const tools: ToolItem[] = allAgents
            .filter((agent) => {
              const matchesTag = agent.tag === categoryTag;
              const isVisible = agent.isVisible !== false;
              return matchesTag && (includeHidden || isVisible);
            })
            .map((agent) => ({
              id: agent.id,
              label: agent.name || i18n.t('common:appShell.useToolCategories.untitledTool'),
              isHidden: !agent.isVisible,
              disabled: agent.configurationStatus?.configured === false,
              disabledReason:
                agent.configurationStatus?.configured === false
                  ? i18n.t('springboard:springboard.toolGrid.notConfigured')
                  : undefined,
            }));

          return {
            id: categoryTag,
            label: CATEGORY_LABELS[categoryTag](),
            tools,
          };
        });
      } catch (error: unknown) {
        logger.error('Failed to fetch tool categories:', error);
        return CATEGORY_TAGS.map((categoryTag) => ({
          id: categoryTag,
          label: CATEGORY_LABELS[categoryTag](),
          tools: [],
        }));
      }
    },
    enabled: !!workspaceId,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: true,
  });

  const managedTools = useVisibleManagedTools();

  const data = useMemo(() => {
    if (!categoriesQuery.data) {
      return undefined;
    }

    // Inject managed tools AFTER the agent permission filter: they carry their
    // own flag/permission gating (via useVisibleManagedTools) and must not be
    // dropped by the agent-only `canOpenTool` check.
    const filteredCategories = injectManagedTools(
      filterByPermissions(categoriesQuery.data),
      managedTools,
    );
    return applyCategoryLabelOverrides(filteredCategories, customizationQuery.data);
  }, [categoriesQuery.data, customizationQuery.data, filterByPermissions, managedTools]);

  return {
    data,
    isPending: categoriesQuery.isPending,
    isLoading: categoriesQuery.isLoading,
    isError: categoriesQuery.isError,
    isFetching: categoriesQuery.isFetching,
    error: categoriesQuery.error,
    refetch: categoriesQuery.refetch,
    status: categoriesQuery.status,
  };
};
